import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Button } from '../ui/button';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Calendar,
  Clock,
  CreditCard,
  CheckCircle,
  Truck,
  Route,
  Navigation,
  Timer,
  RulerIcon,
  ChevronDown,
  Info,
  Star,
  Percent,
  Tag,
  Smartphone,
  AlertCircle,
  TrendingUp,
  Edit,
  Users,
  Image as ImageIcon
} from 'lucide-react';
import { OrderAPI, EarningsAPI } from '../../lib/api';
import { OrderStatus } from '../../lib/types';
import AssignSupplierForm from './AssignSupplierForm';
import AssignTransporterForm from './AssignTransporterForm';
import UpdateStatusForm from './UpdateStatusForm';
import LiveTrackingMap from './LiveTrackingMap';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import EnhancedMapMarker from '../ui/enhanced-map-marker';
import AnimatedPolyline from '../ui/animated-polyline';

// Leaflet icon configuration
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});



interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any | null;
  onOrderUpdated?: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ isOpen, onClose, order: initialOrder, onOrderUpdated }) => {
  const [order, setOrder] = useState<any | null>(initialOrder);
  const [activeTab, setActiveTab] = React.useState('details');
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [deliveryLocation, setDeliveryLocation] = useState<[number, number] | null>(null);
  const [startLocation, setStartLocation] = useState<[number, number] | null>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>([]);
  const [mapZoom, setMapZoom] = useState<number>(10);
  const [routeDuration, setRouteDuration] = useState<string>('');
  const [routeDistance, setRouteDistance] = useState<number>(0);
  const [assignSupplierDialogOpen, setAssignSupplierDialogOpen] = useState(false);
  const [assignTransporterDialogOpen, setAssignTransporterDialogOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapHeight, setMapHeight] = useState<string>('400px');
  // Cancellation-request decision UI state
  const [cancelRefundChoice, setCancelRefundChoice] = useState<'none' | 'full' | 'partial'>('full');
  const [cancelRefundAmount, setCancelRefundAmount] = useState<string>('');
  const [cancelNote, setCancelNote] = useState<string>('');
  const [cancelProcessing, setCancelProcessing] = useState<boolean>(false);

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time helper
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get order status badge variant
  const getOrderStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'confirmed':
        return 'default';
      case 'processing':
        return 'default';
      case 'shipped':
        return 'outline';
      case 'delivered':
        return 'default';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Format order status display
  const formatOrderStatus = (status: string) => {
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Title-case a raw enum/snake_case value for display.
  const titleCase = (value?: string) =>
    (value || '')
      .split('_')
      .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');

  // True for raw Mongo ObjectIds — used to avoid showing UUIDs to admins.
  const isObjectId = (v: any) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);

  // Human-friendly payment method label.
  const formatPaymentMethod = (m?: string) => {
    const map: Record<string, string> = {
      cash: 'Cash on Delivery',
      cod: 'Cash on Delivery',
      online: 'Online Payment',
      razorpay: 'Online (Razorpay)',
      upi: 'UPI',
      card: 'Card',
      netbanking: 'Net Banking',
      bank_transfer: 'Bank Transfer',
      wallet: 'Wallet',
    };
    return m ? (map[m.toLowerCase()] || titleCase(m)) : 'Not specified';
  };

  // Human-friendly payment status label.
  const formatPaymentStatus = (s?: string) => {
    const map: Record<string, string> = {
      completed: 'Paid',
      paid: 'Paid',
      success: 'Paid',
      successful: 'Paid',
      pending: 'Pending',
      failed: 'Failed',
      refunded: 'Refunded',
    };
    return s ? (map[s.toLowerCase()] || titleCase(s)) : 'Pending';
  };

  // Badge variant for a payment status.
  const paymentStatusVariant = (s?: string) => {
    switch ((s || '').toLowerCase()) {
      case 'completed':
      case 'paid':
      case 'success':
      case 'successful':
        return 'success';
      case 'failed':
        return 'destructive';
      case 'refunded':
        return 'secondary';
      default:
        return 'warning';
    }
  };

  // Friendly order-source label.
  const formatOrderSource = (s?: string) => {
    const map: Record<string, string> = {
      mobile_app: 'Mobile App',
      web: 'Website',
      admin: 'Admin Portal',
      supplier_portal: 'Supplier Portal',
    };
    return s ? (map[s.toLowerCase()] || titleCase(s)) : 'N/A';
  };

  // Earnings / payout split (who gets what on this order).
  const [payout, setPayout] = useState<any | null>(null);
  const [payoutLoading, setPayoutLoading] = useState(false);

  const [copied, setCopied] = useState(false);
  const copyOrderId = () => {
    if (!order?._id) return;
    navigator.clipboard?.writeText(order._id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // Update order when prop changes
  useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder]);

  // Reset active tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  // Fetch the earnings/payout split for this order (actuals exist once delivered).
  useEffect(() => {
    if (!isOpen || !order?._id) {
      setPayout(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setPayoutLoading(true);
        const res = await EarningsAPI.getOrderBreakdown(order._id);
        if (!cancelled && res.success) setPayout(res.data);
      } catch {
        if (!cancelled) setPayout(null);
      } finally {
        if (!cancelled) setPayoutLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, order?._id]);

  // Fetch fresh order data
  const fetchOrderDetails = async (orderId: string) => {
    try {
      const response = await OrderAPI.getOrder(orderId);
      if (response.success && response.data) {
        setOrder(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch order details');
    }
  };

  // Handle supplier assignment
  const handleAssignSupplier = async (supplierId: string, itemIds?: string[], notes?: string) => {
    if (!order) return;

    try {
      const requestData = {
        supplierId,
        ...(itemIds && itemIds.length > 0 && { itemIds }),
        ...(notes && notes.trim() !== '' && { notes })
      };

      await OrderAPI.assignSupplier(order._id, requestData);

      // Refresh order data
      await fetchOrderDetails(order._id);

      // Notify parent to refresh list
      if (onOrderUpdated) {
        onOrderUpdated();
      }

      setAssignSupplierDialogOpen(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign supplier');
    }
  };

  // Handle transporter assignment
  const handleAssignTransporter = async (transporterId: string, vehicleDetails?: any, notes?: string) => {
    if (!order) return;

    try {
      const requestData = {
        transporterId,
        ...(vehicleDetails ? { vehicleDetails } : {}),
        ...(notes ? { notes } : {})
      };

      await OrderAPI.assignTransporter(order._id, requestData);

      // Refresh order data
      await fetchOrderDetails(order._id);

      // Notify parent to refresh list
      if (onOrderUpdated) {
        onOrderUpdated();
      }

      setAssignTransporterDialogOpen(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign transporter');
    }
  };

  // Handle status update
  const handleUpdateStatus = async (status: OrderStatus, notes?: string) => {
    if (!order) return;

    try {
      await OrderAPI.updateStatus(order._id, {
        status,
        notes
      });

      // Refresh order data
      await fetchOrderDetails(order._id);

      // Notify parent to refresh list
      if (onOrderUpdated) {
        onOrderUpdated();
      }

      setUpdateStatusDialogOpen(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  // Approve / reject a customer's cancellation request (+ refund decision).
  const handleCancellationDecision = async (decision: 'approve' | 'reject') => {
    if (!order) return;
    setCancelProcessing(true);
    try {
      await OrderAPI.decideCancellation(order._id, {
        decision,
        ...(decision === 'approve'
          ? {
              refundDecision: cancelRefundChoice,
              ...(cancelRefundChoice === 'partial'
                ? { refundAmount: Number(cancelRefundAmount) || 0 }
                : {})
            }
          : {}),
        ...(cancelNote.trim() ? { note: cancelNote.trim() } : {})
      });

      await fetchOrderDetails(order._id);
      if (onOrderUpdated) onOrderUpdated();
      setCancelNote('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process cancellation decision');
    } finally {
      setCancelProcessing(false);
    }
  };

  useEffect(() => {
    if (!order) return;

    let deliveryLoc: [number, number] | null = null;
    let startLoc: [number, number] | null = null;
    let routeCoords: [number, number][] = [];

    // Convert and set locations for OSM
    if (order?.deliveryAddress?.coordinates) {
      deliveryLoc = [
        parseFloat(order.deliveryAddress.coordinates.latitude),
        parseFloat(order.deliveryAddress.coordinates.longitude)
      ];
      setDeliveryLocation(deliveryLoc);
    }

    // Set start location if available - check multiple possible locations for supplier
    if (order?.assignedSupplier?.coordinates?.latitude && order?.assignedSupplier?.coordinates?.longitude) {
      startLoc = [
        parseFloat(order.assignedSupplier.coordinates.latitude),
        parseFloat(order.assignedSupplier.coordinates.longitude)
      ];
      setStartLocation(startLoc);
    } else if (order?.assignedSupplier?.address?.coordinates?.latitude && order?.assignedSupplier?.address?.coordinates?.longitude) {
      startLoc = [
        parseFloat(order.assignedSupplier.address.coordinates.latitude),
        parseFloat(order.assignedSupplier.address.coordinates.longitude)
      ];
      setStartLocation(startLoc);
    } else if (order?.assignedSupplier?.latitude && order?.assignedSupplier?.longitude) {
      startLoc = [
        parseFloat(order.assignedSupplier.latitude),
        parseFloat(order.assignedSupplier.longitude)
      ];
      setStartLocation(startLoc);
    } else if (order?.selectedRoute?.startLocation) {
      // Try to get start location from route data
      startLoc = [
        parseFloat(order.selectedRoute.startLocation.latitude || order.selectedRoute.startLocation.lat),
        parseFloat(order.selectedRoute.startLocation.longitude || order.selectedRoute.startLocation.lng)
      ];
      setStartLocation(startLoc);
    } else if (order?.routeDetails?.geometry?.coordinates?.length > 0) {
      // Use first coordinate from route as start location (fallback)
      const firstCoord = order.routeDetails.geometry.coordinates[0];
      // Check if coordinates need swapping (usually [lon, lat] from MapMyIndia/OSRM)
      // Leaflet expects [lat, lon]
      startLoc = [firstCoord[1], firstCoord[0]];
      setStartLocation(startLoc);
    } else if (order?.selectedRoute?.routeDetails?.geometry?.coordinates?.length > 0) {
      // Use first coordinate from route as start location
      const firstCoord = order.selectedRoute.routeDetails.geometry.coordinates[0];
      startLoc = [firstCoord[1], firstCoord[0]]; // Swap for Leaflet
      setStartLocation(startLoc);
    }

    // Set route coordinates
    // Check both locations for route details
    const routeData = order?.routeDetails || order?.selectedRoute?.routeDetails;

    if (routeData?.geometry?.coordinates) {
      // Swap longitude and latitude for Leaflet (MapMyIndia uses [lon, lat])
      routeCoords = routeData.geometry.coordinates.map(
        (coord: number[]) => [coord[1], coord[0]] as [number, number]
      );
      setRouteCoordinates(routeCoords);

      // Also ensure start/end locations match route endpoints if they were completely missing
      if (!startLoc && routeCoords.length > 0) {
        startLoc = routeCoords[0];
        setStartLocation(startLoc);
      }

      // Extract duration and distance from any available source
      const durationRaw = order.selectedRoute?.duration || routeData?.duration || routeData?.properties?.duration || routeData?.summary?.duration || 0;
      const distanceRaw = order.selectedRoute?.distance || routeData?.distance || routeData?.properties?.distance || routeData?.summary?.distance || 0;

      setRouteDistance(distanceRaw);

      // Convert duration from minutes to hours and minutes
      const totalMinutes = durationRaw;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setRouteDuration(
        hours > 0
          ? `${hours} hr ${minutes} min`
          : `${minutes} min`
      );
    } else if (order?.selectedRoute?.geometry?.coordinates) {
      // Try alternative route coordinates location
      routeCoords = order.selectedRoute.geometry.coordinates.map(
        (coord: number[]) => [coord[1], coord[0]] as [number, number]
      );
      setRouteCoordinates(routeCoords);
    } else if (startLoc && deliveryLoc) {
      // Create a simple route between start and delivery locations
      routeCoords = [startLoc, deliveryLoc];
      setRouteCoordinates(routeCoords);

      // Set a basic duration if available
      const totalMinutes = order?.selectedRoute?.duration || 0;
      if (totalMinutes > 0) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        setRouteDuration(
          hours > 0
            ? `${hours} hr ${minutes} min`
            : `${minutes} min`
        );
      } else {
        setRouteDuration('Direct Route');
        setRouteDistance(0);
      }
    } else {
      setRouteCoordinates([]);
      setRouteDuration('');
      setRouteDistance(0);
    }

    // Calculate map center and zoom
    let centerLat = 0;
    let centerLng = 0;
    let zoomLevel = 10;

    if (routeCoords.length > 1) {
      // Use route bounds if available
      const lats: number[] = routeCoords.map((coord: [number, number]) => coord[0]);
      const lngs: number[] = routeCoords.map((coord: [number, number]) => coord[1]);

      centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

      // Adjust zoom based on route distance
      // Use the newly calculated routeDistance if available, otherwise fallback
      const dist = routeDistance || order.selectedRoute?.distance || 0;
      zoomLevel = dist < 50 ? 12 :
        dist < 100 ? 11 :
          dist < 200 ? 10 : 9;
    } else if (startLoc && deliveryLoc) {
      // Use midpoint between start and delivery if no route
      centerLat = (startLoc[0] + deliveryLoc[0]) / 2;
      centerLng = (startLoc[1] + deliveryLoc[1]) / 2;
      zoomLevel = 11;
    } else if (deliveryLoc) {
      // Use delivery location if only that's available
      centerLat = deliveryLoc[0];
      centerLng = deliveryLoc[1];
      zoomLevel = 12;
    } else if (startLoc) {
      // Use start location if only that's available
      centerLat = startLoc[0];
      centerLng = startLoc[1];
      zoomLevel = 12;
    }

    if (centerLat !== 0 || centerLng !== 0) {
      setMapCenter([centerLat, centerLng]);
      setMapZoom(zoomLevel);
    }
  }, [order]);

  // NOTE: dynamic height calculation relies on routeCoordinates state which is set inside the effect
  // but we can't update state based on state inside useEffect directly without risk of loops 
  // unless we are careful. Since routeCoordinates is set in the SAME effect, it won't be updated yet.
  // We should useEffect on routeCoordinates change to set map height.

  useEffect(() => {
    if (routeCoordinates.length > 1) {
      const lats = routeCoordinates.map(c => c[0]);
      const latDiff = Math.max(...lats) - Math.min(...lats);
      const lngs = routeCoordinates.map(c => c[1]);
      const lngDiff = Math.max(...lngs) - Math.min(...lngs);

      if (latDiff > lngDiff * 1.2) {
        setMapHeight('600px');
      } else {
        setMapHeight('400px');
      }
    }
  }, [routeCoordinates]);

  // Component to auto-fit bounds
  const MapUpdater = ({ coords }: { coords: [number, number][] }) => {
    const map = useMap();

    useEffect(() => {
      if (coords.length > 1) {
        // Create bounds from coordinates
        const bounds = L.latLngBounds(coords);
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 15,
            animate: true
          });
        }
      }
    }, [coords, map]);

    return null;
  };

  // Render map only if locations are available
  const renderMap = () => {
    if (!mapCenter || (mapCenter[0] === 0 && mapCenter[1] === 0)) {
      return null;
    }

    // Prepare supplier information for tooltip
    // Prepare supplier information for tooltip
    // Use optional chaining for safety
    const supplierInfo = order?.assignedSupplier ? {
      name: order.assignedSupplier.name,
      email: order.assignedSupplier.email,
      phone: order.assignedSupplier.mobile
    } : undefined;

    // Prepare delivery information for tooltip
    const deliveryInfo = {
      expectedTime: order?.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : undefined,
      instructions: order?.notes || undefined
    };

    // Prepare route information for tooltip
    const routeInfo = (order?.selectedRoute || order?.routeDetails) ? {
      distance: routeDistance,
      duration: routeDuration
    } : undefined;

    // Format delivery address for tooltip
    const deliveryAddress = order?.deliveryAddress ?
      `${order.deliveryAddress.street}, ${order.deliveryAddress.city}, ${order.deliveryAddress.state} - ${order.deliveryAddress.pincode}`
      : undefined;

    return (
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: mapHeight, width: '100%', transition: 'height 0.3s ease' }}
        maxZoom={15}
        minZoom={5}
      >
        <MapUpdater coords={routeCoordinates} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {startLocation && (
          <EnhancedMapMarker
            position={startLocation}
            type="start"
            title={order?.assignedSupplier?.name || "Supplier Location"}
            address={`${order?.assignedSupplier?.address || 'Supplier Address'}`}
            coordinates={{
              latitude: startLocation[0],
              longitude: startLocation[1]
            }}
            supplierInfo={supplierInfo}
            routeInfo={routeInfo}
            size={[40, 40]}
            zIndex={1003}
            interactive={true}
          />
        )}

        {deliveryLocation && (
          <EnhancedMapMarker
            position={deliveryLocation}
            type="end"
            title={order?.deliveryAddress?.name || "Delivery Location"}
            address={deliveryAddress}
            coordinates={{
              latitude: deliveryLocation[0],
              longitude: deliveryLocation[1]
            }}
            deliveryInfo={deliveryInfo}
            routeInfo={routeInfo}
            size={[40, 40]}
            zIndex={1002}
          />
        )}

        {/* Route polyline */}
        {routeCoordinates.length > 1 && (
          <Polyline
            positions={routeCoordinates}
            color="hsl(24, 100%, 54%)"
            weight={5}
            opacity={0.8}
          />
        )}

        {/* Fallback: Simple line between start and end if no detailed route */}
        {routeCoordinates.length <= 1 && startLocation && deliveryLocation && (
          <Polyline
            positions={[startLocation, deliveryLocation]}
            color="hsl(24, 100%, 54%)"
            weight={3}
            opacity={0.6}
            dashArray="10, 10"
          />
        )}

        {/* AnimatedPolyline - commented out for testing */}
        {/* {routeCoordinates.length > 1 && (
          <AnimatedPolyline
            positions={routeCoordinates}
            color="hsl(24, 100%, 54%)"
            weight={5}
            opacity={0.8}
            animated={true}
            gradient={true}
            animationDuration={3000}
          />
        )} */}
      </MapContainer>
    );
  };

  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-4xl lg:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {/* Mobile-first header layout */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <DialogTitle className="text-lg sm:text-2xl font-bold">
                Order #{order.orderNumber}
              </DialogTitle>
              <StatusBadge status={order.status} className="text-xs" />
              {/* Show awaiting confirmation when pending + supplier assigned */}
              {order.status === 'pending' && order.assignedSupplier && (
                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                  <Clock className="h-3 w-3 mr-1" />
                  Awaiting supplier confirmation
                </Badge>
              )}
            </div>
            {/* Action buttons - stack on mobile, row on desktop */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUpdateStatusDialogOpen(true)}
                disabled={['cancelled', 'rejected', 'refunded'].includes(order.status)}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Update </span>Status
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAssignSupplierDialogOpen(true)}
                disabled={['cancelled', 'rejected', 'refunded'].includes(order.status) || !!order.assignedSupplier}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                {order.assignedSupplier ? 'Supplier ✓' : 'Supplier'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAssignTransporterDialogOpen(true)}
                disabled={['pending', 'cancelled', 'rejected', 'refunded'].includes(order.status) || !!order.transporter}
                className="flex-1 sm:flex-none text-xs sm:text-sm"
              >
                <Truck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                {order.transporter ? 'Transport ✓' : 'Transport'}
              </Button>
            </div>
          </div>
          <DialogDescription className="text-xs sm:text-sm">
            View and manage order details, items, delivery information, and status history.
          </DialogDescription>
          {error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-xs sm:text-sm">
              {error}
            </div>
          )}
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex items-center gap-1 sm:gap-2">
              <Package className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">Details</span>
            </TabsTrigger>
            <TabsTrigger value="items" className="flex items-center gap-1 sm:gap-2">
              <Package className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">Items ({order.items.length})</span>
            </TabsTrigger>
            <TabsTrigger value="delivery" className="flex items-center gap-1 sm:gap-2">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">Delivery</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1 sm:gap-2">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">History</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-3">
            {/* Pending cancellation request — admin approves/rejects + decides refund */}
            {order.cancellationRequest?.status === 'requested' && (
              <Card className="border-amber-300 bg-amber-50">
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="h-5 w-5" />
                    Cancellation Requested
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-amber-900">
                    <span className="font-medium">Reason: </span>
                    {order.cancellationRequest?.reason || '—'}
                  </div>
                  {order.cancellationRequest?.requestedAt && (
                    <div className="text-xs text-amber-700">
                      Requested {new Date(order.cancellationRequest.requestedAt).toLocaleString()}
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Refund decision (applied on approval)</div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {(['full', 'partial', 'none'] as const).map((c) => (
                        <label key={c} className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name="refundChoice"
                            checked={cancelRefundChoice === c}
                            onChange={() => setCancelRefundChoice(c)}
                          />
                          <span>{c === 'none' ? 'No refund' : c === 'full' ? 'Full refund' : 'Partial refund'}</span>
                        </label>
                      ))}
                    </div>
                    {cancelRefundChoice === 'partial' && (
                      <input
                        type="number"
                        min={0}
                        placeholder="Refund amount (₹)"
                        value={cancelRefundAmount}
                        onChange={(e) => setCancelRefundAmount(e.target.value)}
                        className="w-48 rounded-md border px-3 py-1.5 text-sm"
                      />
                    )}
                    <div className="text-xs text-muted-foreground">
                      Order total: ₹{order.totalAmount?.toLocaleString?.() ?? order.totalAmount}
                      {order.paymentDetails?.status ? ` · Payment: ${order.paymentDetails.status}` : ''}
                    </div>
                  </div>

                  <textarea
                    placeholder="Note (optional) — shared as the decision context"
                    value={cancelNote}
                    onChange={(e) => setCancelNote(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                  />

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleCancellationDecision('approve')}
                      disabled={
                        cancelProcessing ||
                        (cancelRefundChoice === 'partial' && !(Number(cancelRefundAmount) > 0))
                      }
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {cancelProcessing ? 'Processing…' : 'Approve cancellation'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleCancellationDecision('reject')}
                      disabled={cancelProcessing}
                    >
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {/* Customer Information */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <User className="h-5 w-5 text-primary" />
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {order.trader?.firstName
                        ? `${order.trader.firstName} ${order.trader.lastName || ''}`.trim()
                        : (order.trader?.name || 'Customer')}
                    </span>
                  </div>
                  {order.trader?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{order.trader.email}</span>
                    </div>
                  )}
                  {(order.trader?.mobile || order.trader?.phoneNumber) && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{order.trader.mobile || order.trader.phoneNumber}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Order Information */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Package className="h-5 w-5 text-primary" />
                    Order Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Order Date:</span>
                      <div className="font-medium">{formatDate(order.createdAt)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Order Time:</span>
                      <div className="font-medium">{formatTime(order.createdAt)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Items:</span>
                      <div className="font-medium">{order.items.length}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Source:</span>
                      <div className="font-medium flex items-center gap-1">
                        <Smartphone className="h-3 w-3" />
                        {formatOrderSource(order.meta?.orderSource)}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Multi-Supplier:</span>
                      <div className="font-medium">
                        {order.isMultiSupplier ? (
                          <Badge variant="secondary">Yes ({order.suppliersCount})</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Updated:</span>
                      <div className="font-medium">{formatDate(order.updatedAt)}</div>
                    </div>
                  </div>

                  {order.expectedDeliveryDate && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Expected Delivery:</span>
                      <div className="font-medium flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatDate(order.expectedDeliveryDate)}
                      </div>
                    </div>
                  )}

                  {order.notes && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Notes:</span>
                      <div className="font-medium bg-accent/50 p-3 rounded-md mt-1">
                        {order.notes}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Discount Information - Collapsible */}
            {order.discount && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Tag className="h-5 w-5 text-green-600" />
                          Discount Applied
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            {order.discount.code}
                          </Badge>
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="bg-green-50 p-4 rounded-md">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Discount Code:</span>
                            <div className="font-medium font-mono">{order.discount.code}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Type:</span>
                            <div className="font-medium capitalize flex items-center gap-1">
                              <Percent className="h-3 w-3" />
                              {order.discount.discountType}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Value:</span>
                            <div className="font-medium">
                              {order.discount.discountType === 'percentage' ? `${order.discount.value}%` : formatCurrency(order.discount.value)}
                            </div>
                          </div>
                          <div className="col-span-2 md:col-span-3">
                            <span className="text-muted-foreground">Discount Amount:</span>
                            <div className="font-bold text-green-600 text-lg">
                              -{formatCurrency(order.discount.discountAmount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Route & Distance Information - Collapsible */}
            {(order.selectedRoute || order.routeDetails) && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Route className="h-5 w-5 text-blue-600" />
                          Route & Distance Information
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="bg-blue-50 p-4 rounded-md">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Distance:</span>
                            <div className="font-medium">{routeDistance} km</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Duration:</span>
                            <div className="font-medium">{routeDuration}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Route Type:</span>
                            <div className="font-medium capitalize">{order.selectedRoute?.routeType || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Provider:</span>
                            <div className="font-medium capitalize">{order.selectedRoute?.provider || 'N/A'}</div>
                          </div>
                        </div>
                      </div>

                      {order.lockedDistanceInfo && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="text-sm">
                            <span className="text-muted-foreground">Distance Locked At:</span>
                            <div className="font-medium">{formatDate(order.lockedDistanceInfo.lockedAt)} at {formatTime(order.lockedDistanceInfo.lockedAt)}</div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Customer Rating & Review */}
            {order.rating?.rating && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Star className="h-5 w-5 text-yellow-500" />
                    Customer Rating & Review
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-yellow-50 p-4 rounded-md space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-5 w-5 ${star <= order.rating.rating
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold text-lg">{order.rating.rating}/5</span>
                    </div>
                    {order.rating.comment && (
                      <p className="text-sm text-muted-foreground italic">
                        “{order.rating.comment}”
                      </p>
                    )}
                    {order.rating.createdAt && (
                      <p className="text-xs text-muted-foreground">
                        Reviewed on {formatDate(order.rating.createdAt)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Support Reference — order source + a copyable ID for support tickets */}
            <Collapsible>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Info className="h-5 w-5 text-gray-600" />
                        Support Reference
                      </div>
                      <ChevronDown className="h-4 w-4" />
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Order Number:</span>
                          <div className="font-medium">{order.orderNumber}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Ordered Via:</span>
                          <div className="font-medium">{formatOrderSource(order.meta?.orderSource)}</div>
                        </div>
                        {order.meta?.appVersion && (
                          <div>
                            <span className="text-muted-foreground">App Version:</span>
                            <div className="font-medium">{order.meta.appVersion}</div>
                          </div>
                        )}
                        {order.meta?.deviceInfo && (
                          <div>
                            <span className="text-muted-foreground">Device:</span>
                            <div className="font-medium">{order.meta.deviceInfo}</div>
                          </div>
                        )}
                        <div className="md:col-span-2">
                          <span className="text-muted-foreground">Internal reference (for support):</span>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="font-mono text-xs bg-white px-2 py-1 rounded border flex-1 truncate">
                              {order._id}
                            </code>
                            <Button variant="outline" size="sm" onClick={copyOrderId} className="text-xs">
                              {copied ? 'Copied' : 'Copy'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Supplier Confirmations - Collapsible */}
            {order.supplierConfirmations && order.supplierConfirmations.length > 0 && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          Supplier Confirmations ({order.supplierConfirmations.length})
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        {order.supplierConfirmations.map((confirmation: any, index: number) => (
                          <div key={index} className="bg-green-50 p-4 rounded-md">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Supplier:</span>
                                <div className="font-medium">{confirmation.supplierName}</div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Confirmed At:</span>
                                <div className="font-medium">{formatDate(confirmation.confirmedAt)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Supplier Information */}
            {order.assignedSupplier && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Truck className="h-5 w-5 text-primary" />
                    Assigned Supplier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-accent/50 p-4 rounded-md">
                    <div className="font-medium text-lg">{order.assignedSupplier.name}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="h-4 w-4" />
                        {order.assignedSupplier.email}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="h-4 w-4" />
                        {order.assignedSupplier.mobile}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Information */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Amount breakdown — how the order total is calculated */}
                <div className="rounded-md border bg-muted/20 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                    How the total is calculated
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Items subtotal (goods)</span>
                      <span className="font-medium tabular-nums">{formatCurrency(order.subtotal || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">+ GST / Tax</span>
                      <span className="font-medium tabular-nums">{formatCurrency(order.taxAmount || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">+ Delivery / Transport</span>
                      <span className="font-medium tabular-nums">{formatCurrency(order.deliveryCharge || 0)}</span>
                    </div>
                    {(order.discountAmount || 0) > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>− Discount{order.discount?.code ? ` (${order.discount.code})` : ''}</span>
                        <span className="font-medium tabular-nums">−{formatCurrency(order.discountAmount || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2 mt-1 text-base font-bold">
                      <span>Order total</span>
                      <span className="text-primary tabular-nums">{formatCurrency(order.totalAmount || 0)}</span>
                    </div>
                  </div>
                </div>

                {order.paymentDetails && (
                  <div className="mt-4 pt-4 border-t bg-accent/50 p-4 rounded-md">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Payment Method:</span>
                        <div className="font-medium">{formatPaymentMethod(order.paymentDetails.method)}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Payment Status:</span>
                        <div className="mt-0.5">
                          <StatusBadge status={order.paymentDetails.status} />
                        </div>
                      </div>
                      {(() => {
                        // A paid/completed online order paid the full total. Some orders
                        // recorded paidAmount as total/100 (Razorpay paise↔rupee mix-up),
                        // so for settled payments we show the order total as the source of truth.
                        const settled = ['completed', 'paid', 'success', 'successful']
                          .includes((order.paymentDetails.status || '').toLowerCase());
                        const raw = order.paymentDetails.paidAmount;
                        const total = order.totalAmount ?? 0;
                        const amountPaid = settled
                          ? total
                          : (typeof raw === 'number' && total > 0 && Math.abs(raw * 100 - total) <= total * 0.02
                              ? raw * 100
                              : raw);
                        if (typeof amountPaid !== 'number' || amountPaid <= 0) return null;
                        return (
                          <div>
                            <span className="text-muted-foreground">Amount Paid:</span>
                            <div className="font-medium">{formatCurrency(amountPaid)}</div>
                          </div>
                        );
                      })()}
                      {order.paymentDetails.paidAt && (
                        <div>
                          <span className="text-muted-foreground">Paid On:</span>
                          <div className="font-medium">{formatDate(order.paymentDetails.paidAt)}</div>
                        </div>
                      )}
                      {order.paymentDetails.transactionId && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Transaction Reference:</span>
                          <div className="font-medium font-mono text-xs">{order.paymentDetails.transactionId}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Earnings & Payout Split — who gets what on this order (actuals only) */}
            {(() => {
              const hasEarnings = !!(payout && (
                (Array.isArray(payout.supplier) && payout.supplier.length > 0) ||
                payout.transporter || payout.platformEarnings
              ));
              const total = order.totalAmount ?? 0;
              const supplierAmt = payout?.breakdown?.totalSupplierPayments ?? 0;
              const transporterAmt = payout?.breakdown?.totalTransporterPayments ?? 0;
              const platformAmt = payout?.breakdown?.platformCommission ?? 0;
              const recorded = supplierAmt + transporterAmt + platformAmt;
              const unallocated = Math.round((total - recorded) * 100) / 100;
              const reconciles = Math.abs(unallocated) < 1;

              const rows = [
                { icon: <Users className="h-4 w-4 text-blue-600" />, label: 'Supplier(s)', note: 'Goods value', amount: supplierAmt, color: 'text-blue-600' },
                { icon: <Truck className="h-4 w-4 text-green-600" />, label: 'Transporter', note: 'Transport fee', amount: transporterAmt, color: 'text-green-600' },
                { icon: <TrendingUp className="h-4 w-4 text-purple-600" />, label: 'Platform', note: 'Commission', amount: platformAmt, color: 'text-purple-600' },
              ];

              return (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="flex items-center justify-between text-base">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-primary" />
                        Earnings &amp; Payout Split
                      </div>
                      {hasEarnings ? (
                        <Badge variant="outline" className={reconciles
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-amber-50 text-amber-700 border-amber-300'}>
                          {reconciles ? 'Final' : 'Partial'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">Pending</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {!hasEarnings ? (
                      <div className="rounded-md border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                        Payouts are calculated when the order is delivered. Each party’s share
                        will appear here and match what they see in their own app.
                      </div>
                    ) : (
                      <>
                        <div className="divide-y rounded-md border text-sm">
                          {rows.map((r, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center gap-2.5">
                                <div className="rounded-md bg-muted/50 p-1.5">{r.icon}</div>
                                <div>
                                  <div className="font-medium leading-tight">{r.label}</div>
                                  <div className="text-xs text-muted-foreground leading-tight">{r.note}</div>
                                </div>
                              </div>
                              <div className={`font-bold tabular-nums ${r.color}`}>{formatCurrency(r.amount)}</div>
                            </div>
                          ))}
                          {!reconciles && unallocated > 0 && (
                            <div className="flex items-center justify-between px-3 py-2 bg-amber-50/60">
                              <div className="flex items-center gap-2.5">
                                <div className="rounded-md bg-amber-100 p-1.5">
                                  <AlertCircle className="h-4 w-4 text-amber-600" />
                                </div>
                                <div>
                                  <div className="font-medium leading-tight text-amber-800">Unallocated</div>
                                  <div className="text-xs text-amber-700 leading-tight">Not yet attributed (e.g. transporter unassigned)</div>
                                </div>
                              </div>
                              <div className="font-bold tabular-nums text-amber-700">{formatCurrency(unallocated)}</div>
                            </div>
                          )}
                          <div className="flex items-center justify-between px-3 py-2 bg-muted/20">
                            <span className="font-semibold">Order total</span>
                            <span className="font-bold tabular-nums">{formatCurrency(total)}</span>
                          </div>
                        </div>

                        {reconciles && platformAmt === 0 && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            No separate platform commission on this order — the platform margin is built into pricing.
                          </p>
                        )}

                        {Array.isArray(payout?.supplier) && payout.supplier.length > 1 && (
                          <div className="mt-3 space-y-1">
                            <div className="text-xs font-medium text-muted-foreground">Per supplier</div>
                            {payout.supplier.map((s: any, idx: number) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{s.entityName || 'Supplier'}</span>
                                <span className="font-medium tabular-nums">{formatCurrency(s.amount || 0)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>

          <TabsContent value="items" className="space-y-4">
            <Card>
              <CardHeader className="py-3">
                <CardTitle>Order Items ({order.items.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-accent/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                          Product
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                          Pricing
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                          Total
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {order.items.map((item: any, index: number) => (
                        <tr key={item._id || index} className="hover:bg-accent/30">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              {item.productImage && (
                                <img
                                  src={item.productImage}
                                  alt={item.productName}
                                  className="w-16 h-16 object-cover rounded-md border"
                                />
                              )}
                              <div className="flex-1">
                                <div className="font-medium text-base">
                                  {item.productName || (typeof item.product === 'object' && item.product ? item.product.name : '')}
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {typeof item.product === 'object' && item.product ? item.product.description : ''}
                                </div>
                                {typeof item.product === 'object' && item.product?.category &&
                                  !isObjectId(item.product.category) && (
                                  <Badge variant="outline" className="mt-2 text-xs">
                                    Category: {typeof item.product.category === 'object'
                                      ? (item.product.category.name || '')
                                      : item.product.category}
                                  </Badge>
                                )}
                                {item.assignedSupplier && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Supplier: {item.assignedSupplier.name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <Badge variant="outline" className="font-medium">
                              {item.quantity} {item.unit}
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1 text-sm">
                              <div>
                                <span className="text-muted-foreground">Base:</span>
                                <span className="font-medium ml-1">{formatCurrency(item.basePrice)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Unit:</span>
                                <span className="font-medium ml-1">{formatCurrency(item.pricePerUnit)}</span>
                              </div>
                              {item.discountRate > 0 && (
                                <div className="text-green-600">
                                  <span className="text-muted-foreground">Discount:</span>
                                  <span className="font-medium ml-1">{item.discountRate}%</span>
                                </div>
                              )}
                              {item.taxRate > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Tax:</span>
                                  <span className="font-medium ml-1">{item.taxRate}%</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 font-bold text-primary text-lg">
                            {formatCurrency(item.totalPrice)}
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-2">
                              {item.supplierConfirmed !== undefined && (
                                <Badge variant={item.supplierConfirmed ? "default" : "secondary"}>
                                  {item.supplierConfirmed ? "Confirmed" : "Pending"}
                                </Badge>
                              )}
                              {item.pricingDiscrepancy && (
                                <Badge variant="destructive" className="flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  Price Issue
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Item Summary */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-blue-50 rounded-md">
                    <div className="text-2xl font-bold text-blue-600">{order.items.length}</div>
                    <div className="text-muted-foreground">Total Items</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-md">
                    <div className="text-2xl font-bold text-green-600">
                      {order.items.reduce((sum: number, item: any) => sum + item.quantity, 0)}
                    </div>
                    <div className="text-muted-foreground">Total Quantity</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-md">
                    <div className="text-2xl font-bold text-yellow-600">
                      {order.items.filter((item: any) => item.supplierConfirmed).length}
                    </div>
                    <div className="text-muted-foreground">Confirmed</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-md">
                    <div className="text-2xl font-bold text-red-600">
                      {order.items.filter((item: any) => item.pricingDiscrepancy).length}
                    </div>
                    <div className="text-muted-foreground">Price Issues</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="delivery" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Address */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="h-5 w-5 text-primary" />
                    Delivery Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="font-medium text-lg">{order.deliveryAddress.name}</div>
                    <div className="text-muted-foreground">
                      <div>{order.deliveryAddress.street}</div>
                      <div>
                        {order.deliveryAddress.city}, {order.deliveryAddress.state} - {order.deliveryAddress.pincode}
                      </div>
                    </div>
                    {order.deliveryAddress.coordinates && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${order.deliveryAddress.coordinates.latitude},${order.deliveryAddress.coordinates.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline pt-2 border-t mt-2"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        Open in Google Maps
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Route Information */}
              {order.selectedRoute && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Route className="h-5 w-5 text-primary" />
                      Route Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Distance:</span>
                          <div className="font-medium">{order.selectedRoute.distance} km</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Duration:</span>
                          <div className="font-medium">{routeDuration}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Route Type:</span>
                          <div className="font-medium capitalize">{order.selectedRoute.routeType}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Provider:</span>
                          <div className="font-medium capitalize">{order.selectedRoute.provider}</div>
                        </div>
                      </div>

                      {order.selectedRoute.routeDetails?.tollCost > 0 && (
                        <div className="pt-2 border-t">
                          <span className="text-muted-foreground">Toll Cost:</span>
                          <div className="font-medium">{formatCurrency(order.selectedRoute.routeDetails.tollCost)}</div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Live tracking map — driver position + suggested route */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Navigation className="h-5 w-5 text-primary" />
                  Live Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LiveTrackingMap orderId={order._id} active={activeTab === 'delivery'} />
              </CardContent>
            </Card>

            {/* Proof of delivery — photos captured by the driver at handover */}
            {Array.isArray(order.proofOfDelivery?.images) && order.proofOfDelivery.images.length > 0 && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    Proof of Delivery
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {order.proofOfDelivery.images.map((img: any, i: number) => {
                      const url = typeof img === 'string' ? img : (img?.url || img?.imageUrl || '');
                      if (!url) return null;
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block aspect-square overflow-hidden rounded-lg border bg-muted/30"
                        >
                          <img src={url} alt={`Delivery proof ${i + 1}`} className="h-full w-full object-cover hover:scale-105 transition-transform" />
                        </a>
                      );
                    })}
                  </div>
                  {order.proofOfDelivery?.timestamp && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Captured {new Date(order.proofOfDelivery.timestamp).toLocaleString()}
                    </p>
                  )}
                  {order.proofOfDelivery?.notes && (
                    <p className="mt-1 text-xs text-muted-foreground">{order.proofOfDelivery.notes}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-5 w-5 text-primary" />
                  Status History ({order.statusHistory?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.statusHistory && order.statusHistory.length > 0 ? (
                  <div className="space-y-4">
                    {order.statusHistory
                      .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                      .map((history: any, index: number) => {
                        const isLatest = index === 0;
                        const statusColors: Record<string, string> = {
                          pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                          confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
                          processing: 'bg-purple-100 text-purple-800 border-purple-200',
                          ready_for_pickup: 'bg-indigo-100 text-indigo-800 border-indigo-200',
                          picked_up: 'bg-cyan-100 text-cyan-800 border-cyan-200',
                          in_transit: 'bg-orange-100 text-orange-800 border-orange-200',
                          delivered: 'bg-green-100 text-green-800 border-green-200',
                          completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                          cancelled: 'bg-red-100 text-red-800 border-red-200',
                          rejected: 'bg-red-100 text-red-800 border-red-200',
                          refunded: 'bg-gray-100 text-gray-800 border-gray-200'
                        };

                        return (
                          <div
                            key={index}
                            className={`relative flex items-start gap-4 pb-4 ${index !== order.statusHistory.length - 1 ? 'border-b' : ''
                              } ${isLatest ? 'bg-accent/30 p-4 rounded-lg' : ''}`}
                          >
                            {/* Timeline line */}
                            {index !== order.statusHistory.length - 1 && (
                              <div className="absolute left-4 top-12 w-0.5 h-8 bg-border"></div>
                            )}

                            <div className="flex-shrink-0">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isLatest
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background border-border'
                                }`}>
                                <CheckCircle className="h-4 w-4" />
                              </div>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <Badge
                                  variant="outline"
                                  className={`${statusColors[history.status] || 'bg-gray-100 text-gray-800'} font-medium`}
                                >
                                  {formatOrderStatus(history.status)}
                                  {isLatest && <span className="ml-1 text-xs">(Current)</span>}
                                </Badge>
                                <div className="text-sm text-muted-foreground">
                                  <div>{formatDate(history.timestamp)}</div>
                                  <div className="text-xs">{formatTime(history.timestamp)}</div>
                                </div>
                              </div>

                              {history.notes && (
                                <div className="mt-2 p-3 bg-accent/50 rounded-md">
                                  <p className="text-sm">{history.notes}</p>
                                </div>
                              )}

                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                {history.updatedBy && (
                                  <div className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    <span>Updated by {history.userType || 'System'}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No status history available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Timeline Summary */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Order Timeline Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-3 bg-blue-50 rounded-md">
                    <div className="font-medium text-blue-600">Order Created</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDate(order.createdAt)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTime(order.createdAt)}
                    </div>
                  </div>

                  <div className="text-center p-3 bg-green-50 rounded-md">
                    <div className="font-medium text-green-600">Last Updated</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDate(order.updatedAt)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTime(order.updatedAt)}
                    </div>
                  </div>

                  {order.expectedDeliveryDate && (
                    <div className="text-center p-3 bg-yellow-50 rounded-md">
                      <div className="font-medium text-yellow-600">Expected Delivery</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatDate(order.expectedDeliveryDate)}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Assignment Forms */}
        <AssignSupplierForm
          isOpen={assignSupplierDialogOpen}
          onClose={() => {
            setAssignSupplierDialogOpen(false);
            setError(null);
          }}
          onAssign={handleAssignSupplier}
          orderItems={order?.items || []}
        />

        <AssignTransporterForm
          isOpen={assignTransporterDialogOpen}
          onClose={() => {
            setAssignTransporterDialogOpen(false);
            setError(null);
          }}
          onAssign={handleAssignTransporter}
        />

        {order && (
          <UpdateStatusForm
            isOpen={updateStatusDialogOpen}
            onClose={() => {
              setUpdateStatusDialogOpen(false);
              setError(null);
            }}
            onUpdate={handleUpdateStatus}
            currentStatus={order.status}
          />
        )}
      </DialogContent>
    </Dialog >
  );
};

export default OrderDetailModal;
